import { capitalCase } from "text-case";
import {
  ControlledComboBox,
  ControlledDataGrid,
  ControlledDataGridColumnProps,
  ControlledInput,
  ControlledTextArea,
  FormFor,
  FormSection,
} from "@excent-one/react/form";
import { ContentLayout, ContentHeader } from "@excent-one/react/layout/content";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "@excent-one/react/notification";
import { dialogBox } from "@excent-one/react";
import FormActionsBar from "@excent-one/react/form/layouts/FormActionsBar";
import { Button, FormFieldSet } from "@progress/kendo-react-all";
import {
  calendarDateIcon,
  cancelIcon,
  checkIcon,
  pencilIcon,
  saveIcon,
  undoIcon,
} from "@progress/kendo-svg-icons";
import {
  approveMonthlyCoveragePlan,
  cancelMonthlyCoveragePlan,
  getAllAreas,
  getAllBranches,
  getAllMonthlyCoveragePlanStatuses,
  getMonthlyCoveragePlanById,
  getMyDetails,
  getMyMembers,
  ListOfMcpSiteAssignmentDto,
  McpSiteAssignmentDto,
  MonthlyCoveragePlanDto,
  reviseMonthlyCoveragePlan,
  SaveMonthlyCoveragePlan,
  saveMonthlyCoveragePlan,
  submitMonthlyCoveragePlan,
} from "@phrmpc/infrastructure/phrmpc";
import { useQuery } from "@tanstack/react-query";
import { formatDate, parseDate } from "@progress/kendo-intl";
import { validateSchedules } from "../utils/ValidateSchedules";
import { DateTime } from "luxon";
import { useAuthContext } from "@excent-one/auth";
import { DEPLOYMENT_ACTIONS, MCP_STATUS_OPTIONS, MONTHS_OPTIONS, YEARS_OPTIONS } from "@phrmpc/shared";

const DRAFT_STATUS_ID = MCP_STATUS_OPTIONS.find((s) => s.code === "DRAFT")?.id;
const FOR_APPROVAL_STATUS_ID = MCP_STATUS_OPTIONS.find((s) => s.code === "FOR-APR")?.id;
const FOR_REVISION_STATUS_ID = MCP_STATUS_OPTIONS.find((s) => s.code === "FOR-REV")?.id;

interface MonthlyCoveragePlan {
  id?: string;
  coordinatorId?: string;
  coordinatorName?: string;
  monthId?: number;
  monthName?: string;
  year?: number;
  areaId?: number;
  areaName?: string;
  remarks?: string;
  statusId?: number;
  statusName?: string;
  mcpSiteAssignments?: SiteAssignment[];
}

interface SiteAssignment {
  id?: string;
  monthlyCoveragePlanId?: string;
  date?: Date | null;
  branchId?: number | string | null;
  branchName?: string | null;
  fromSchedule?: Date | null;
  toSchedule?: Date | null;
  isDeleted?: boolean;
}

const { Form, useFormRef } = FormFor<MonthlyCoveragePlan>();

const MonthlyCoveragePlanForm = () => {
  const { id } = useParams({ strict: false });
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();
  const navigate = useNavigate();
  const formRef = useFormRef();
  const [loading, setLoading] = useState(false);

  const location = useLocation();

  const isDisplayOnly = location.pathname.includes("/display");

  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserManager = isInAnyRole("Manager");

  const minSchedDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      1,
    ),
    maxSchedDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 2,
      0,
    );
  const [initialValues, setInitialValues] = useState<
    MonthlyCoveragePlan | undefined
  >(undefined);
  const initialAssignmentsRef = useRef<SiteAssignment[]>([]);

  const { data: areaOptionsData } = useQuery({
    queryKey: ["areas"],
    queryFn: () => getAllAreas(),
  });
  const areaOptions = areaOptionsData ?? [];

  const { data: branchOptions = [] } = useQuery({
    queryKey: ["branches"],
    queryFn: () => getAllBranches(),
  });

  const { data: statusOptions } = useQuery({
    queryKey: ["status"],
    queryFn: () => getAllMonthlyCoveragePlanStatuses(),
  });

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const payload = (formValues: MonthlyCoveragePlan): MonthlyCoveragePlanDto => {
    formValues.monthName = MONTHS_OPTIONS.find(
      (m) => m.id == formValues.monthId,
    )?.name;
    formValues.areaName = areaOptions.find(
      (a) => a.id == formValues.areaId,
    )?.name;

    const toTime = (date: Date): string => {
      return date.toTimeString().slice(0, 8);
    };

    const assignments: McpSiteAssignmentDto[] | undefined =
      formValues?.mcpSiteAssignments?.map((s) => {
      const branchName = branchOptions.find((b) => b.id === s.branchId)?.name;

      return {
        ...s,
        branchName,
        date: s.date ?? undefined,
        fromSchedule: s.fromSchedule ? toTime(s.fromSchedule) : null,
        toSchedule: s.toSchedule ? toTime(s.toSchedule) : null,
      };
    });

    const monthlyCoveragePlan: MonthlyCoveragePlanDto = {
      ...formValues,
      mcpSiteAssignments: assignments,
    };

    return monthlyCoveragePlan;
  };

  const { data: directMembers } = useQuery({
    queryKey: ["my-members", currentUser?.id],
    queryFn: () => getMyMembers({
        payload: {
          pageSize: 10000,
        },
      } as any)
  });

  const isDirectMember = useMemo(() => {
    const memberIds = new Set((directMembers?.items ?? []).map((m) => m.id));
    return (coordinatorId?: string) => !!coordinatorId && memberIds.has(coordinatorId);
  }, [directMembers?.items]);


  const buttonAvailability = (
    button: (typeof DEPLOYMENT_ACTIONS)[keyof typeof DEPLOYMENT_ACTIONS],
    monthlyCoveragePlan: MonthlyCoveragePlan,
  ) => {
    switch (button) {
      case DEPLOYMENT_ACTIONS.Save:
        return !(
          !isDisplayOnly &&
          monthlyCoveragePlan.coordinatorId == myDetails?.id &&
          [DRAFT_STATUS_ID, FOR_REVISION_STATUS_ID].includes(monthlyCoveragePlan?.statusId ?? 0)
        );
      case DEPLOYMENT_ACTIONS.Submit:
      case DEPLOYMENT_ACTIONS.Cancel:
      case DEPLOYMENT_ACTIONS.Edit:
        return !(
          isDisplayOnly &&
          monthlyCoveragePlan.coordinatorId == myDetails?.id &&
          [DRAFT_STATUS_ID, FOR_REVISION_STATUS_ID].includes(monthlyCoveragePlan?.statusId ?? 0)
        );
      case DEPLOYMENT_ACTIONS.ForRevision:
      case DEPLOYMENT_ACTIONS.Approve:
        return !(
          monthlyCoveragePlan?.statusId == FOR_APPROVAL_STATUS_ID &&
          ((isUserAreaHead && isDirectMember(monthlyCoveragePlan.coordinatorId)) || isUserManager) &&
          monthlyCoveragePlan.coordinatorId != myDetails?.id
        );
      default:
        break;
    }
  };

  const combineDateAndTime = (date: Date, time: string): Date => {
    const [hours, minutes, seconds = 0] = time.split(":").map(Number);

    return DateTime.fromJSDate(date)
      .set({
        hour: hours,
        minute: minutes,
        second: 0,
        millisecond: 0,
      })
      .toJSDate();
  };

  const assignmentsDisplay = (
    assignments: ListOfMcpSiteAssignmentDto | undefined,
  ): SiteAssignment[] | undefined => {
    return assignments
      ?.map((s) => {
        const isMinDate =
          s.date?.getFullYear() === 1 &&
          s.date?.getMonth() === 0 &&
          s.date?.getDate() === 1 &&
          s.date?.getHours() === 0 &&
          s.date?.getMinutes() === 0 &&
          s.date?.getSeconds() === 0 &&
          s.date?.getMilliseconds() === 0;
        const date = s.date && !isMinDate ? new Date(s.date) : null;

        const scheduleDate = date ?? DateTime.now().toJSDate();

        const fromSchedule = s.fromSchedule
          ? combineDateAndTime(scheduleDate, s.fromSchedule)
          : null;

        const toSchedule = s.toSchedule
          ? combineDateAndTime(scheduleDate, s.toSchedule)
          : null;

        return {
          ...s,
          date,
          fromSchedule,
          toSchedule,
        };
      })
      .sort((a, b) => {
        const dateDiff = (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0);

        if (dateDiff !== 0) {
          return dateDiff;
        }

        return (
          (a.fromSchedule?.getTime() ?? 0) - (b.fromSchedule?.getTime() ?? 0)
        );
      });
  };

  const loadData = async (id: string | undefined) => {
    if (!id) return;

    const monthlyCoveragePlan: MonthlyCoveragePlanDto =
      await getMonthlyCoveragePlanById(id);

    const assignments =
      assignmentsDisplay(monthlyCoveragePlan?.mcpSiteAssignments) ?? [];

    const mcp: MonthlyCoveragePlan = {
      ...monthlyCoveragePlan,
      mcpSiteAssignments: assignments,
    };

    setInitialValues(mcp);
    initialAssignmentsRef.current = structuredClone(assignments);
  };

  useEffect(() => {
    const init = async () => {
      if (id) {
        await loadData(id);
      } else {
        const now = new Date();
        now.setDate(1);
        now.setMonth(now.getMonth() + 1);
        const nextMonth = now.getMonth() + 1;
        const nextYear = now.getFullYear();

        const defaultValues: MonthlyCoveragePlan = {
          coordinatorId: currentUser?.id,
          coordinatorName: currentUser?.displayName,
          monthId: nextMonth,
          monthName: now.toLocaleString("default", { month: "long" }),
          areaId: areaOptions[0].id,
          areaName: areaOptions[0].name,
          year: nextYear,
          statusId: 1,
          statusName: "Draft",
          mcpSiteAssignments: [],
        };
        setInitialValues(defaultValues);
      }
    };

    init();
  }, [areaOptions]);

  const handleSave = useCallback(
    async (values: MonthlyCoveragePlan): Promise<MonthlyCoveragePlan> => {
      try {
        if (values.coordinatorId && values.year && values.monthId) {
          const payloadData = payload(values);
          const saveMonthlyCoveragePlanPayload: SaveMonthlyCoveragePlan = {
            payload: payloadData,
          };

          if (id) {
            const newData = await saveMonthlyCoveragePlan(
              id,
              saveMonthlyCoveragePlanPayload,
            );

            const assignments =
              assignmentsDisplay(newData.mcpSiteAssignments) ?? [];

            const mcp: MonthlyCoveragePlan = {
              ...newData,
              mcpSiteAssignments: assignments,
            };
            setInitialValues(mcp);
            initialAssignmentsRef.current = structuredClone(assignments);

            notification.success("Monthly Coverage Plan saved successfully.");
          }
        } else {
          notification.error(
            "Unable to save Monthly Coverage Plan. Please try again.",
          );
        }

        return values;
      } catch (e) {
        notification.error(
          id
            ? "Unable to save Monthly Coverage Plan. Please try again."
            : "Unable to save draft. Please try again.",
        );
        throw e;
      }
    },
    [id, areaOptions, branchOptions, navigate],
  );

  const handleApprove = useCallback(
    async (values: MonthlyCoveragePlan): Promise<void> => {
      dialogBox.confirm({
        title: "Approve Monthly Coverage Plan Request?",
        content: (
          <>Are you sure you want to approve Monthly Coverage Plan Request?</>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await approveMonthlyCoveragePlan(id ?? "", {
                  payload: payload(values),
                });
                notification.success(
                  "Monthly Coverage Plan approved successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to approve Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [id, navigate],
  );

  const validate = (form: any) => {
    const data = form.formValues as MonthlyCoveragePlan;
    const errors: Record<string, string> = {};

    const payloadData = payload(data);

    const schedValidations = validateSchedules(
      payloadData.mcpSiteAssignments ?? [],
    );
    if (schedValidations.length) {
      schedValidations.forEach((schedValidation) => {
        notification.info(schedValidation.message);
        errors.sched = "true";
      });
    }

    if (!payloadData.remarks) {
      notification.info("Remarks is required.");
      errors.remarks = "true";
    }

    if (!payloadData.areaId) {
      notification.info("Area is required.");
      errors.area = "true";
    }

    return errors;
  };

  const handleSubmit = useCallback(
    async (values: MonthlyCoveragePlan): Promise<MonthlyCoveragePlan> => {
      dialogBox.confirm({
        title: "Submit Monthly Coverage Plan Request?",
        content: (
          <>Are you sure you want to submit Monthly Coverage Plan Request?</>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await submitMonthlyCoveragePlan(id ?? "", {
                  payload: payload(values),
                });
                notification.success(
                  "Monthly Coverage Plan submitted successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to submit Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
      return values;
    },
    [id, navigate],
  );

  const handleRevise = useCallback(
    async (values: MonthlyCoveragePlan): Promise<void> => {
      dialogBox.confirm({
        title: "Set Monthly Coverage Plan Request to For Revision?",
        content: (
          <>
            Are you sure you want to set Monthly Coverage Plan Request to for
            revision?
          </>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await reviseMonthlyCoveragePlan(id ?? "", {
                  payload: payload(values),
                });
                notification.success(
                  "Monthly Coverage Plan is set to for revision successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable to set Monthly Coverage Plan to for revision. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [navigate],
  );

  const viewMyCalendar = useCallback(
    (values: MonthlyCoveragePlan) => {
      if (!formRef.current) return;

      const proceedToCalendar = (
        month: number | undefined,
        year: number | undefined,
      ) => navigate({ to: `/deployment/my-calendar/${month}/${year}` });

      if (formRef.current?.formState.isDirty) {
        dialogBox.confirm({
          title: "Leave without saving?",
          content: (
            <>You have unsaved changes. Are you sure you want to leave?</>
          ),
          actions: {
            confirm: {
              text: "Yes, leave",
              onClick: () => proceedToCalendar(values.monthId, values.year),
            },
            cancel: { text: "No" },
          },
        });
      } else {
        proceedToCalendar(values.monthId, values.year);
      }
    },
    [navigate],
  );

  const handleCancel = useCallback(
    async (values: MonthlyCoveragePlan): Promise<void> => {
      dialogBox.confirm({
        title: "Cancel Monthly Coverage Plan Request?",
        content: (
          <>Are you sure you want to cancel Monthly Coverage Plan Request?</>
        ),
        actions: {
          confirm: {
            text: "Yes.",
            onClick: async () => {
              try {
                await cancelMonthlyCoveragePlan(id ?? "", {
                  payload: payload(values),
                });
                notification.success(
                  "Monthly Coverage Plan is cancelled successfully.",
                );

                await loadData(id);
              } catch (error) {
                notification.error(
                  "Unable cancel Monthly Coverage Plan. Please try again.",
                );
              }
            },
          },
          cancel: { text: "No" },
        },
      });
    },
    [navigate],
  );

  const areAssignmentsEqual = (
    current: SiteAssignment[],
    original: SiteAssignment[],
  ): boolean => {
    if (current.length !== original.length) {
      return false;
    }

    const normalize = (assignments: SiteAssignment[]) =>
      assignments
        .map((x) => ({
          id: x.id ?? null,
          monthlyCoveragePlanId: x.monthlyCoveragePlanId ?? null,
          date: x.date?.getTime() ?? null,
          branchId: x.branchId ?? null,
          fromSchedule: x.fromSchedule?.getTime() ?? null,
          toSchedule: x.toSchedule?.getTime() ?? null,
          isDeleted: x.isDeleted ?? false,
        }))
        .sort((a, b) => String(a.id ?? "").localeCompare(String(b.id ?? "")));

    return (
      JSON.stringify(normalize(current)) === JSON.stringify(normalize(original))
    );
  };

  const back = useCallback(
    (values: MonthlyCoveragePlan) => {
      const currentAssignments = values.mcpSiteAssignments ?? [];
      const originalAssignments = initialAssignmentsRef.current;

      const assignmentsChanged = !areAssignmentsEqual(
        currentAssignments,
        originalAssignments,
      );

      const hasNewChanges =
        Object.keys(formRef.current?.formState?.dirtyFields ?? {}).length > 0 ||
        assignmentsChanged;

      if (!formRef.current) return;

      const proceedBack = () =>
        navigate({ to: "/deployment/monthly-coverage-plan" });

      if (hasNewChanges) {
        dialogBox.confirm({
          title: "Leave without saving?",
          content: (
            <>You have unsaved changes. Are you sure you want to leave?</>
          ),
          actions: {
            confirm: { text: "Yes, leave", onClick: proceedBack },
            cancel: { text: "No" },
          },
        });
      } else {
        proceedBack();
      }
    },
    [navigate],
  );

  const mcpSiteAssignmentColumns = useMemo<
    ControlledDataGridColumnProps<SiteAssignment>[]
  >(
    () => [
      {
        field: "date",
        title: "Date",
        editor: "date",
        editorProps: {
          min: minSchedDate,
          max: maxSchedDate,
        },
        display: ({ value }: { value: Date | null | undefined }) => {
          if (!value) return <span />;
          return <span>{formatDate(value, "MM/dd/yyyy")}</span>;
        },
      },
      {
        field: "branchId",
        title: "Location / Branch",
        editor: "combobox",
        editorProps: {
          data: branchOptions,
          dataItemKey: "id",
          textField: "name",
        },
      },
      {
        field: "fromSchedule",
        title: "Schedule Time From",
        editor: "time",
        editorProps: (dataItem) => {
          const { toSchedule, date } = dataItem.dataItem;

          let maxDate: Date | undefined;

          if (toSchedule) {
            maxDate = new Date(toSchedule);
            maxDate.setMinutes(maxDate.getMinutes() - 15);

            if (date) {
              const scheduleDate = new Date(date);

              maxDate.setFullYear(
                scheduleDate.getFullYear(),
                scheduleDate.getMonth(),
                scheduleDate.getDate(),
              );
              maxDate.setSeconds(0, 0);
            }
          }
          return {
            format: "hh:mm a",
            steps: { hour: 0, minute: 15, second: 0 },
            max: maxDate,
            inputAttributes: {
              readOnly: true,
            },
          };
        },
        display: ({ value }: any) => (
          <span>{formatDate(parseDate(value), "hh:mm a")}</span>
        ),
      },
      {
        field: "toSchedule",
        title: "Schedule Time To",
        editor: "time",
        editorProps: (dataItem) => {
          const { fromSchedule, date } = dataItem.dataItem;

          let minDate: Date | undefined;

          if (fromSchedule) {
            minDate = new Date(fromSchedule);
            minDate.setMinutes(minDate.getMinutes() + 15);

            if (date) {
              const scheduleDate = new Date(date);

              minDate.setFullYear(
                scheduleDate.getFullYear(),
                scheduleDate.getMonth(),
                scheduleDate.getDate(),
              );
              minDate.setSeconds(0, 0);
            }
          }
          return {
            format: "hh:mm a",
            steps: { hour: 0, minute: 15, second: 0 },
            min: minDate,
            inputAttributes: {
              readOnly: true,
            },
          };
        },
        display: ({ value }: any) => (
          <span>{formatDate(parseDate(value), "hh:mm a")}</span>
        ),
      },
    ],
    [branchOptions],
  );

  return (
    <ContentLayout>
      <ContentHeader
        title={`${capitalCase(id ? "display" : "create")} Monthly Coverage Plan Request`}
        breadcrumb
        loading={loading}
        back
      />
      <Form
        ref={formRef}
        onSubmit={handleSubmit}
        validate={validate}
        values={initialValues}
        reValidateMode="onSubmit"
      >
        {({ control, getValues }) => (
          <>
            <FormSection title="Header" loading={loading}>
              <FormFieldSet cols={2}>
                <ControlledComboBox
                  control={control}
                  name="monthId"
                  label="Month"
                  data={MONTHS_OPTIONS}
                  dataItemKey="id"
                  textField="code"
                  readOnly
                />
                <ControlledComboBox
                  control={control}
                  name="year"
                  label="Year"
                  data={YEARS_OPTIONS}
                  dataItemKey="id"
                  textField="code"
                  readOnly
                />
                <ControlledInput
                  control={control}
                  name="coordinatorName"
                  label="Coordinator Name"
                  readOnly
                />
                {areaOptionsData && (
                  <ControlledComboBox
                    control={control}
                    name="areaId"
                    label="Area"
                    data={areaOptionsData}
                    dataItemKey="id"
                    textField="name"
                    readOnly
                    required={!isDisplayOnly}
                  />
                )}
                {statusOptions && (
                  <ControlledComboBox
                    control={control}
                    name="statusId"
                    label="Status"
                    data={statusOptions}
                    dataItemKey="id"
                    textField="name"
                    readOnly
                  />
                )}
              </FormFieldSet>
            </FormSection>
            <FormSection title={`Site Assignments${isDisplayOnly ? "" : "*"}`}>
              <FormFieldSet>
                <ControlledDataGrid
                  fieldArrayName="mcpSiteAssignments"
                  columns={mcpSiteAssignmentColumns}
                  readOnly={isDisplayOnly}
                  canAdd={!isDisplayOnly}
                  commands={isDisplayOnly ? [] : undefined}
                  control={control}
                  dataItemKey="id"
                  loading={loading}
                />
              </FormFieldSet>
            </FormSection>
            <FormSection>
              <FormFieldSet>
                <ControlledTextArea
                  name="remarks"
                  control={control}
                  loading={loading}
                  label="Remarks"
                  readOnly={isDisplayOnly}
                  required={!isDisplayOnly}
                />
              </FormFieldSet>
            </FormSection>
            <FormActionsBar loading={loading}>
              <Button
                type="button"
                svgIcon={saveIcon}
                themeColor="primary"
                onClick={() => handleSave(getValues())}
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.Save, getValues())}
              >
                Save
              </Button>
              <Button
                type="button"
                svgIcon={pencilIcon}
                themeColor="base"
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.Edit, getValues())}
                onClick={(e) => {
                  e.preventDefault();
                  navigate({
                    to: "/deployment/monthly-coverage-plan/$id/edit",
                    params: { id: id! },
                  });
                }}
              >
                Edit
              </Button>
              <Button
                type="submit"
                fillMode="outline"
                svgIcon={checkIcon}
                themeColor="secondary"
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.Submit, getValues())}
              >
                Submit
              </Button>
              <Button
                type="button"
                fillMode="outline"
                svgIcon={undoIcon}
                themeColor="secondary"
                onClick={() => handleRevise(getValues())}
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.ForRevision, getValues())}
              >
                For Revision
              </Button>
              <Button
                type="button"
                fillMode="outline"
                svgIcon={checkIcon}
                themeColor="secondary"
                onClick={() => handleApprove(getValues())}
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.Approve, getValues())}
              >
                Approve
              </Button>
              <Button
                type="button"
                fillMode="outline"
                svgIcon={cancelIcon}
                themeColor="secondary"
                onClick={() => handleCancel(getValues())}
                hidden={buttonAvailability(DEPLOYMENT_ACTIONS.Cancel, getValues())}
              >
                Cancel
              </Button>
              <Button
                type="button"
                fillMode="outline"
                svgIcon={calendarDateIcon}
                themeColor="secondary"
                onClick={() => viewMyCalendar(getValues())}
              >
                View My Calendar
              </Button>
              <Button
                type="button"
                fillMode="outline"
                svgIcon={cancelIcon}
                themeColor="secondary"
                onClick={() => back(getValues())}
              >
                Back
              </Button>
            </FormActionsBar>
          </>
        )}
      </Form>
    </ContentLayout>
  );
};

export default MonthlyCoveragePlanForm;
